import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import { fetchTimingAdjusted, fetchTimingHeatmap, type FetchOpts } from '../../services/contactOptimizationService';
import type { TimingAdjusted, TimingHeatmap } from '../../types/contactOptimization';
import { formatDateRange } from '../../utils/date';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';
import ContactOptimizationNav from './ContactOptimizationNav';
import { RefreshProvider, useRefreshRegistration, useReportFreshness } from './RefreshController';
import ChartSectionHeader from './ChartSectionHeader';
import ClockBadge from './ClockBadge';
import { SectionScopeChip } from './ScopeBanner';
import ExplainerNote, { ExplainerToggle } from './ExplainerNote';
import RecommendedWindowsSection from './recommendedWindowsTable';
import {
  BandLegend,
  CompareStatesChart,
  PooledByHourChart,
  TimingHeatmapChart,
  type TimingHeatmapMetric,
} from './timingCharts';

const MAX_COMPARE_STATES = 5;

// Same brand-colored "selected" treatment used for menu items and sidebar
// nav elsewhere in the app (theme/index.ts) -- MUI's own color="primary"
// selected state is a barely-visible 8% tint, too subtle to read as "this
// is the active choice" at a glance, so this makes the selected pill match
// how selection already looks everywhere else in the product.
const selectedToggleSx = (t: import('@mui/material').Theme) => ({
  '& .MuiToggleButton-root': { fontWeight: 500 },
  '& .Mui-selected': {
    fontWeight: 700,
    backgroundColor: `${t.tokens.brand.primarySoft} !important`,
    color: `${t.tokens.text.accent} !important`,
    borderColor: alpha(t.palette.primary.main, 0.4),
  },
});

function exportHeatmapCsv(heatmap: TimingHeatmap) {
  const header = [
    'state', 'hour',
    'connect_rate_adjusted', 'connect_rate_confidence_low', 'connect_rate_confidence_high', 'connect_rate_band', 'actual_connect_rate', 'total_calls', 'total_connects', 'calls_this_cell_is_based_on',
    'value_cost_ratio', 'value_cost_confidence_low', 'value_cost_confidence_high', 'value_cost_band', 'deal_rate', 'typical_deal_size_usd', 'leads_dialed_for_value_cost',
  ];
  const lines = [header.join(',')];
  for (const row of heatmap.grid) {
    for (const cell of row.cells) {
      lines.push(
        [
          row.state,
          cell.hour_label,
          cell.shrunk_rate?.toFixed(4) ?? '',
          cell.ci_low?.toFixed(4) ?? '',
          cell.ci_high?.toFixed(4) ?? '',
          cell.band ?? '',
          cell.connect_rate?.toFixed(4) ?? '',
          cell.total_calls,
          cell.total_connects,
          cell.prior_weight_calls ?? '',
          cell.value_cost_ratio?.toFixed(4) ?? '',
          cell.value_cost_ci_low?.toFixed(4) ?? '',
          cell.value_cost_ci_high?.toFixed(4) ?? '',
          cell.value_cost_band ?? '',
          cell.deal_rate?.toFixed(4) ?? '',
          cell.typical_deal_size?.toFixed(2) ?? '',
          cell.leads_dialed_value_cost,
        ].join(','),
      );
    }
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'timing-heatmap.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/** "hours 9am-8pm" -- the window the grid covers, stated by when it CLOSES
 * rather than by its last column's name.
 *
 * Each column is the hour BEGINNING at its label, so the last one, "7pm",
 * is the 7-8pm slot and calls placed in it are inside the customer's 9am-8pm
 * policy. Naming the range by its final column read as "we stop at 7pm"
 * and lost an hour of the day that is genuinely callable.
 */
function callingWindowCloses(hours: { hour: number; hour_label: string }[]): string {
  const last = hours[hours.length - 1];
  if (!last) return '8pm';
  const closes = (last.hour + 1) % 24;
  const suffix = closes >= 12 ? 'pm' : 'am';
  return `${closes % 12 === 0 ? 12 : closes % 12}${suffix}`;
}

function callingWindowRange(hours: { hour: number; hour_label: string }[]): string {
  const first = hours[0];
  if (!first) return '9am\u20138pm';
  return `${first.hour_label}\u2013${callingWindowCloses(hours)}`;
}

function callingWindowLabel(hours: { hour: number; hour_label: string }[]): string {
  return `hours ${callingWindowRange(hours)}`;
}

export default function TimingPage() {
  return (
    <RefreshProvider>
      <TimingPageContent />
    </RefreshProvider>
  );
}

function TimingPageContent() {
  const feature = useFeature('contact_optimization');
  const [heatmap, setHeatmap] = React.useState<TimingHeatmap | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showAll, setShowAll] = React.useState(false);

  const [adjusted, setAdjusted] = React.useState<TimingAdjusted | null>(null);
  const [adjustedError, setAdjustedError] = React.useState<string | null>(null);
  const [adjustedStates, setAdjustedStates] = React.useState<string[] | null>(null);
  const [compareStates, setCompareStates] = React.useState<string[]>([]);
  const [heatmapMetric, setHeatmapMetric] = React.useState<TimingHeatmapMetric>('both');
  const [showLineHelp, setShowLineHelp] = React.useState(false);
  const [showCompareHelp, setShowCompareHelp] = React.useState(false);
  const [showGridHelp, setShowGridHelp] = React.useState(false);

  // Default to the top 3 states by opportunity (heatmap.grid's own sort
  // order), and drop any picked state that falls out of the current grid
  // (e.g. after toggling Top-N vs. All states) so the Autocomplete never
  // holds a value outside its own options list.
  React.useEffect(() => {
    if (!heatmap) return;
    const available = new Set(heatmap.grid.map((r) => r.state));
    setCompareStates((prev) => {
      const kept = prev.filter((s) => available.has(s));
      if (kept.length > 0) return kept;
      return heatmap.grid.slice(0, 3).map((r) => r.state);
    });
  }, [heatmap]);

  // Remembers whatever `states` scope (top-N vs. all) was last requested,
  // so Refresh can replay the exact same scope instead of silently
  // resetting back to the top-3 default.
  const lastStatesRef = React.useRef<string[] | undefined>(undefined);

  const load = React.useCallback((states?: string[], opts?: FetchOpts) => {
    lastStatesRef.current = states;
    setLoading(true);
    setError(null);
    return fetchTimingHeatmap(states, opts)
      .then(setHeatmap)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load Timing'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (!feature.enabled) return;
    load();
  }, [feature.enabled, load]);

  // The model's "hour's own effect" curve now only feeds "Compare
  // states" below (a real vs. model-implied line pair for whichever
  // states you pick) -- fetched automatically whenever a model is
  // available, with no user action needed. Still re-fetches if the set
  // of states shown changes (top-N vs. all).
  React.useEffect(() => {
    if (!heatmap?.model_available) return;
    const states = heatmap.grid.map((r) => r.state);
    const same =
      adjustedStates &&
      adjustedStates.length === states.length &&
      adjustedStates.every((s, i) => s === states[i]);
    if (same) return;
    setAdjustedError(null);
    fetchTimingAdjusted(states)
      .then((res) => {
        setAdjusted(res);
        setAdjustedStates(states);
      })
      .catch((e: unknown) => setAdjustedError(e instanceof Error ? e.message : 'Failed to load model check'));
  }, [heatmap, adjustedStates]);

  // Refresh needs to force the "adjusted" model-check line to recompute
  // too -- the effect above only re-fetches it when the *set of states*
  // changes, which a same-scope refresh never does.
  useRefreshRegistration(
    React.useCallback(async () => {
      await load(lastStatesRef.current, { refresh: true });
      if (!heatmap?.model_available) return;
      const states = lastStatesRef.current ?? heatmap.grid.map((r) => r.state);
      if (states.length === 0) return;
      try {
        const res = await fetchTimingAdjusted(states, { refresh: true });
        setAdjusted(res);
        setAdjustedStates(states);
        setAdjustedError(null);
      } catch (e: unknown) {
        setAdjustedError(e instanceof Error ? e.message : 'Failed to load model check');
      }
    }, [load, heatmap]),
  );
  useReportFreshness(heatmap?.computed_at);

  if (!feature.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <ContactOptimizationNav active="timing" />
        <Alert severity="info">
          Contact Optimization is not turned on for this workspace. Enable it under Settings &rarr; Features.
        </Alert>
      </Box>
    );
  }

  if (loading && !heatmap) {
    return (
      <Box>
        <ContactOptimizationNav active="timing" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <ContactOptimizationNav active="timing" />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!heatmap) return null;

  const setStateScope = (all: boolean) => {
    if (all === showAll) return;
    setShowAll(all);
    load(all ? heatmap.all_states.map((s) => s.state) : undefined);
  };

  // Dropped into every section's own header below via `SectionScopeChip`,
  // rather than stated once at the top -- Timing has no exceptions (every
  // section really does share this one window), but each still names it
  // directly instead of asking the reader to remember a banner above.
  const historyWindow = {
    label: 'Full call history',
    detail: `${formatDateRange(heatmap.date_range.min_call_dt, heatmap.date_range.max_call_dt)} \u00b7 every lead, channel, product \u00b7 ${callingWindowLabel(heatmap.hours)}`,
  };

  return (
    <Box>
      <ContactOptimizationNav active="timing" />
      <SettingsPageHeader description="Timing answers 'when should we be calling' — which hour of the day, per state, is worth dialing." />

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Show:</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          color="primary"
          value={heatmapMetric}
          onChange={(_e, v: TimingHeatmapMetric | null) => v && setHeatmapMetric(v)}
          sx={selectedToggleSx}
        >
          <ToggleButton value="connect">How likely to connect (90+ sec. connect)</ToggleButton>
          <ToggleButton value="value">How much it's worth (Value/Cost Ratio)</ToggleButton>
          <ToggleButton value="both">Both</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          Changes every chart on this page
        </Typography>
      </Stack>

      {/* Just the guardrail warning now -- the state-count caption that used
          to live here duplicated both the scope banner above (lead-local
          time) and the "States shown" control now under the heatmap below,
          so it was dropped rather than kept in sync in three places. */}
      {heatmap.concentration_guardrail.states_below_floor_count > 0 && (
        <Stack sx={{ mb: 2 }}>
          <Tooltip
            title={`These states have enough leads (${heatmap.concentration_guardrail.min_volume_to_check.toLocaleString()}+ calls) but were only called during fewer than ${heatmap.concentration_guardrail.hours_per_state_floor} of the ${heatmap.hours.length} hours we measured: ${heatmap.concentration_guardrail.states_below_floor.join(', ')}. A state should ideally be called across a real spread of hours, not just one or two, before we trust its "best hour" too much.`}
            arrow
          >
            <Chip
              label={`${heatmap.concentration_guardrail.states_below_floor_count} state(s) called at too few hours`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
            />
          </Tooltip>
        </Stack>
      )}

      {adjustedError && <Alert severity="error" sx={{ mb: 2 }}>{adjustedError}</Alert>}

      <Paper
        elevation={0}
        sx={(t) => ({ p: 2, mb: 2, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px` })}
      >
        <ChartSectionHeader
          title={(() => {
            const scope = showAll ? `all ${heatmap.all_states.length} states` : `top ${heatmap.default_state_count} states`;
            if (heatmapMetric === 'value') return `How much it's worth by hour, ${scope}`;
            if (heatmapMetric === 'both') return `Connect rate & Value/Cost Ratio by hour, ${scope}`;
            return `Connect rate by hour, ${scope}`;
          })()}
          subtitle={`Every state ${showAll ? 'currently shown' : `in the top ${heatmap.default_state_count}`} added together into one line, hour by hour. Follows the "States shown" toggle below the grid.`}
          actions={
            <>
              <SectionScopeChip window={historyWindow} />
              <ClockBadge basis="lead_local" />
              <ExplainerToggle title="What this line shows" open={showLineHelp} onClick={() => setShowLineHelp((v) => !v)} />
            </>
          }
        />
        <ExplainerNote title="What this line shows" open={showLineHelp}>
          {`This adds up every state's calls into one overall trend line${heatmapMetric === 'both' ? ' (two lines, one per metric)' : ''}, showing how ${heatmapMetric === 'value' ? "the dollar value of dialing" : heatmapMetric === 'both' ? 'connect rate and dollar value' : 'the connect rate'} moves across the day company-wide — the same question the grid below answers state-by-state, but as a single ${heatmapMetric === 'both' ? 'chart' : 'line'} instead of ${heatmap.grid.length} rows. It only reflects the ${showAll ? 'states currently shown ("All states")' : `top ${heatmap.default_state_count} states by opportunity`} below, and follows the "Show" toggle at the top of the page.${heatmapMetric !== 'both' ? ' The dashed line marks the middle (median) hour\u2019s value.' : ''}`}
        </ExplainerNote>
        <PooledByHourChart heatmap={heatmap} metric={heatmapMetric} />
      </Paper>

      <Paper
        elevation={0}
        sx={(t) => ({ p: 2, mb: 2, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px` })}
      >
        <ChartSectionHeader
          title="Compare states"
          subtitle={`Lines up to ${MAX_COMPARE_STATES} states you pick, side by side, so their hour-by-hour patterns are easy to compare.`}
          actions={
            <>
              <SectionScopeChip window={historyWindow} />
              <ClockBadge basis="lead_local" />
              <ExplainerToggle
                title="Why compare lines instead of reading grid rows"
                open={showCompareHelp}
                onClick={() => setShowCompareHelp((v) => !v)}
              />
              <Autocomplete
                multiple
                size="small"
                options={heatmap.grid.map((r) => r.state)}
                value={compareStates}
                onChange={(_e, newValue) => setCompareStates(newValue.slice(0, MAX_COMPARE_STATES))}
                getOptionDisabled={(option) => compareStates.length >= MAX_COMPARE_STATES && !compareStates.includes(option)}
                renderInput={(params) => <TextField {...params} placeholder={'Add a state\u2026'} />}
                sx={{ minWidth: 280, maxWidth: 420 }}
              />
            </>
          }
        />
        <ExplainerNote title="Why compare lines instead of reading grid rows" open={showCompareHelp}>
          {`Two states can have very different patterns across the day: one might peak sharply in the morning, another might stay flat, another might be best in the evening. Scanning two far-apart rows of the grid and mentally comparing their shapes is hard. This puts up to ${MAX_COMPARE_STATES} states' lines directly on top of each other so their timing patterns are easy to compare at a glance — useful when deciding which of a few states should get priority coverage at a given hour. It also follows the "Show" toggle at the top of the page, so switching that toggle shows these same states compared by dollar value instead of connect rate.`}
        </ExplainerNote>
        <CompareStatesChart heatmap={heatmap} adjusted={adjusted} states={compareStates} metric={heatmapMetric} />
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Paper
          elevation={0}
          sx={(t) => ({
            p: 2,
            border: `1px solid ${t.tokens.border.subtle}`,
            borderRadius: `${t.ds.radius.md}px`,
            position: 'relative',
          })}
        >
          <ChartSectionHeader
            title="Best times to call, by state & hour"
            subtitle={
              <>
                {/* The window is stated here, in the one place a reader
                    actually looks, because the column headers cannot say it:
                    each is the hour BEGINNING at its label, so the grid ends
                    at a column called "7pm" that runs until 8pm. Without
                    this line the page reads as though calling stops an hour
                    before it does. */}
                Every state and hour from {callingWindowRange(heatmap.hours)}, their local time — colored by
                how good a time it is to call. Each column is the hour that starts at its label, so
                &ldquo;{heatmap.hours[heatmap.hours.length - 1]?.hour_label ?? '7pm'}&rdquo; covers up to{' '}
                {callingWindowCloses(heatmap.hours)}.
                {heatmap.submit_to_paid_rate != null &&
                  ` "How much it's worth" assumes ${(heatmap.submit_to_paid_rate * 100).toFixed(1)}% of submitted premium ends up paid.`}
              </>
            }
            actions={
              <>
                <SectionScopeChip window={historyWindow} />
                <ClockBadge basis="lead_local" />
                <Tooltip title="Download this grid as a CSV file — state, hour, connect rate, value/cost ratio, bands, call and deal volume" arrow>
                  <IconButton size="small" onClick={() => exportHeatmapCsv(heatmap)} aria-label="Download heatmap as CSV">
                    <DownloadRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <ExplainerToggle title="How to read the grid below" open={showGridHelp} onClick={() => setShowGridHelp((v) => !v)} />
              </>
            }
          />

          <ExplainerNote title="How to read the grid below" open={showGridHelp}>
            {`Each cell is one **state, at one hour of the day** (the hour where the lead actually lives, not wherever the agent is sitting). Use the "Show" toggle at the top of the page to choose what the colors mean:

- **How likely to connect (90+ sec. connect)** — darker blue means someone is more likely to pick up and stay on the line for at least 90 seconds (our definition of a "connect"); red means less likely.
- **How much it's worth (Value/Cost Ratio)** — how many dollars of paid premium we'd expect back for every dollar spent dialing that slot. A ratio of 2x means "every $1 spent dialing this slot brings back about $2 in paid premium, over time." This is worth more than just "does the phone get answered": a slot with a mediocre connect rate can still be a strong opportunity here if the applications it produces are big enough.
- **Both** (the default) — shows both numbers in each cell, colored by whichever of the two looks best for that hour. A state with a so-so connect rate can still show up as a strong opportunity here if the deals it produces are big enough to be worth it, the same logic the original recommendation spreadsheet used, just applied per state instead of one floor-wide cutoff.

**How Value/Cost Ratio is actually calculated:** four numbers multiplied together, divided by one more: \`(chance a dialed lead submits an application) \u00d7 (typical dollar size of a submitted application, for that state) \u00d7 (% of submitted premium that historically ends up actually paid, company-wide) \u00f7 (average cost to acquire the leads being dialed in that slot)\`. *Real example, CA at 7am:* about 19% of leads dialed there go on to submit an application, a typical CA application is worth about $3,820 in premium, 26% of submitted premium company-wide ends up actually paid, and it cost about $45 on average to acquire those leads. That's \`0.19 \u00d7 $3,820 \u00d7 0.26 \u00f7 $45 \u2248 4.2x\`, matching the 4.2x shown on that cell.

None of these are just the raw numbers. With only a few hundred calls (or a handful of completed deals) in a cell, a raw rate can swing wildly and mislead you. Every cell is blended with that state's own overall average first, leaning more on the average when a cell has thin data and more on the actual number when it has real volume, at a ${Math.round(heatmap.ci_level * 100)}% confidence level — how sure we are that a color reflects a real, lasting pattern, not just this particular batch of calls. Hover any cell for the exact numbers and why it's colored that way.

States are ranked by an **opportunity score** (how big the gap is between a state's best and worst hours, weighted by how many calls that gap is based on, and how confident we are in it). Only the top ${heatmap.default_state_count} show by default; "All states" shows the rest, including ones with less data.

${heatmap.model_available ? `Want to check whether a good-looking hour is really about the hour, or just about which leads happen to land there? Switch the toggle above to "How likely to connect," then pick a few states in "Compare states" below — it lays the model's hour-only view over the real numbers for exactly those states.` : `Once a model is trained (Predictive Models), "Compare states" below will also show each state's hour-only view — whether a good-looking hour is really about the hour, or about which leads happen to land in it.`}`}
          </ExplainerNote>

          <Divider sx={{ mb: 1.5 }} />

          <BandLegend metric={heatmapMetric} />
          <TimingHeatmapChart heatmap={heatmap} metric={heatmapMetric} />

          {/* Which states to include -- sits below the grid it controls, at the
              bottom-right, out of the way of the header actions above. */}
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              States shown:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              color="primary"
              value={showAll ? 'all' : 'top'}
              disabled={loading}
              onChange={(_e, v: 'top' | 'all' | null) => v && setStateScope(v === 'all')}
              sx={selectedToggleSx}
            >
              <ToggleButton value="top">{`Top ${heatmap.default_state_count} states`}</ToggleButton>
              <ToggleButton value="all">{`All ${heatmap.all_states.length} states`}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>
      </Box>

      <RecommendedWindowsSection />
    </Box>
  );
}
